/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Lock, User, Calendar, Globe, ArrowLeft, UserRound, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { AVAILABLE_LANGUAGES } from '@/i18n';
import Logo from '@/components/Logo';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PatientRegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    date_of_birth: '',
    health_data_consent: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const changeLang = (code) => { i18n.changeLanguage(code); localStorage.setItem('sc_lang', code); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.full_name) return;
    if (!form.health_data_consent) {
      toast.error(t('patientConsentRequired'));
      return;
    }
    setLoading(true);
    try {
      await register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        full_name: form.full_name.trim(),
        category: 'paziente',
        role: 'patient',
        date_of_birth: form.date_of_birth || null,
        health_data_consent: true,
      });
      toast.success(t('registerSuccess'));
      navigate('/telemedicine');
    } catch (err) {
      const msg = err.response?.data?.detail || t('errorOccurred');
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background bg-grain flex items-center justify-center px-4 py-8" data-testid="patient-register-page">
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}
        className="w-full max-w-lg"
      >
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="patient-register-back">
            <ArrowLeft className="h-4 w-4" /> {t('welcomeBackChoice')}
          </Link>
          <div className="flex items-center gap-3">
            <Logo size={36} withText={false} />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-[11px] px-3 py-1.5 rounded-full uppercase font-semibold bg-white/70 hover:bg-white flex items-center gap-1">
                <Globe className="h-3 w-3" /> {i18n.language.toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
              {AVAILABLE_LANGUAGES.map(l => (
                <DropdownMenuItem key={l.code} onClick={() => changeLang(l.code)} className={i18n.language === l.code ? 'font-semibold text-primary' : ''}>
                  <span className="mr-2 text-base">{l.flag}</span>{l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>

        <Card className="p-6 rounded-2xl bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 text-emerald-700 flex items-center justify-center">
              <UserRound className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display font-semibold text-xl" data-testid="patient-register-title">{t('patientRegisterTitle')}</h1>
              <p className="text-xs text-muted-foreground">{t('patientRegisterSubtitle')}</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4 mt-5" data-testid="patient-register-form">
            <div>
              <Label className="text-xs text-muted-foreground">{t('fullName')}</Label>
              <div className="mt-1 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} className="pl-9" data-testid="patient-fullname-input" required autoComplete="name" />
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">{t('email')}</Label>
              <div className="mt-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="pl-9" data-testid="patient-email-input" required autoComplete="email" />
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">{t('password')}</Label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} className="pl-9" data-testid="patient-password-input" required minLength={6} autoComplete="new-password" />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{t('patientPasswordHint')}</p>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">{t('patientDob')}</Label>
              <div className="mt-1 relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} className="pl-9" data-testid="patient-dob-input" />
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{t('patientDobOptional')}</p>
            </div>

            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.health_data_consent}
                  onCheckedChange={(v) => set('health_data_consent', !!v)}
                  className="mt-0.5"
                  data-testid="patient-consent-checkbox"
                />
                <div className="text-xs leading-relaxed">
                  <span className="font-semibold flex items-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" />
                    {t('patientConsentTitle')}
                  </span>
                  <span className="text-muted-foreground">{t('patientConsentBody')}</span>
                </div>
              </label>
            </div>

            <Button type="submit" className="w-full" disabled={loading || !form.health_data_consent} data-testid="patient-register-submit">
              {loading ? t('loading') : t('patientCreateAccount')}
            </Button>
          </form>

          <div className="mt-5 text-sm text-center text-muted-foreground">
            {t('alreadyHaveAccount')} <Link to="/login" className="text-primary font-medium hover:underline" data-testid="patient-goto-login">{t('loginNow')}</Link>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default PatientRegisterPage;
